import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { RoleGuideBanner } from '../components/RoleGuideBanner';
import { Button } from '../components/ui/button';
import {
  Briefcase, Users, CheckCircle, Clock, ArrowRight, Calendar,
  Shield, FileCheck, AlertCircle, XCircle, Eye, ChevronDown, ChevronUp, MapPin,
  MessageSquare, UserCog, Save, Globe, Award, Code, Copy, ExternalLink, HelpCircle,
  Activity
} from 'lucide-react';
import { toast } from 'sonner';
import { useCurrencySettings } from '../utils/currency';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import SocialShareButtons from '../components/SocialShareButtons';
import AvailabilityCalendar from '../components/AvailabilityCalendar';
import GettingStartedGuide from '../components/GettingStartedGuide';
import RecoveryDiscountBanner from '../components/RecoveryDiscountBanner';
import AffiliateWidget from '../components/AffiliateWidget';
import DashboardTabs from '../components/DashboardTabs';
import InviteTeaserCard from '../components/InviteTeaserCard';
import LocalProsPanel from '../components/LocalProsPanel';
import ProAffiliateWallet from '../components/ProAffiliateWallet';
import { useQueryTab } from '../hooks/useQueryTab';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const EmbedCodeSection = () => {
  const [embedData, setEmbedData] = useState(null);
  const [loadingEmbed, setLoadingEmbed] = useState(true);
  const [requestSent, setRequestSent] = useState(false);
  const [requestStatus, setRequestStatus] = useState(null);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const fetchEmbed = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/agent/embed-code`);
        setEmbedData(res.data);
      } catch (err) { logger.error('Failed to load embed data:', err); }
      setLoadingEmbed(false);
    };
    fetchEmbed();
  }, []);

  const requestEmbed = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/agent/request-embed`, { website_url: websiteUrl });
      setRequestSent(true);
      setRequestStatus(res.data.status);
      toast.success(res.data.message);
    } catch (err) {
      toast.error('Failed to send request. Please try again.');
    }
    setSending(false);
  };

  if (loadingEmbed) return <div className="text-center py-8 text-slate-400">Loading...</div>;

  const slug = embedData?.profile_url?.split('/agent/')[1] || '';
  const agentName = embedData?.agent_name || 'Professional';

  return (
    <div className="space-y-6" data-testid="embed-section">
      {/* Social Share */}
      <div className="luxury-card p-6">
        <SocialShareButtons profileUrl={embedData?.profile_url || ''} agentName={agentName} slug={slug} />
      </div>
      {/* Profile Link */}
      <div className="luxury-card p-6">
        <h3 className="text-lg font-semibold text-[#0F172A] mb-2 flex items-center gap-2"><ExternalLink className="h-5 w-5 text-[#E07A3A]" />Your Public Profile</h3>
        <p className="text-sm text-slate-500 mb-4">This is your profile page on Shopperden. Preview it below — this is what your customers will see.</p>
        <div className="flex gap-2">
          <input readOnly value={embedData?.profile_url || ''} className="flex-1 bg-slate-50 border border-slate-200 px-3 py-2 text-sm text-slate-600 font-mono" data-testid="profile-url" />
          <a href={embedData?.profile_url} target="_blank" rel="noopener noreferrer">
            <Button className="bg-[#0F172A] text-white hover:bg-[#1e293b] text-xs tracking-wider" data-testid="preview-profile-btn">
              <Eye className="h-3.5 w-3.5 mr-1.5" />Preview
            </Button>
          </a>
        </div>
      </div>

      {/* Request Embed Widget — simplified for non-tech professionals */}
      <div className="luxury-card p-6" data-testid="request-embed-section">
        <h3 className="text-lg font-semibold text-[#0F172A] mb-2 flex items-center gap-2">
          <Code className="h-5 w-5 text-[#E07A3A]" />
          Website Widget
        </h3>
        <p className="text-sm text-slate-500 mb-4">
          Have your own website? We can add a Shopperden widget that shows your profile and services directly on your site. Our team will set it up for you — no technical knowledge needed.
        </p>
        {(() => {
          if (requestSent || requestStatus === 'pending') return (
            <div className="bg-emerald-50 border border-emerald-200 p-4 flex items-start gap-3" data-testid="request-sent-msg">
              <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-emerald-700">Request Received</p>
                <p className="text-xs text-emerald-600 mt-1">Our team will set up your website widget and contact you within 24 hours. No action needed from your side.</p>
              </div>
            </div>
          );
          if (requestStatus === 'completed') return (
            <div className="bg-blue-50 border border-blue-200 p-4 flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-blue-700">Widget Already Set Up</p>
                <p className="text-xs text-blue-600 mt-1">Your website widget has been configured. Check your email or contact our team if you need any changes.</p>
              </div>
            </div>
          );
          return (
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-500 block mb-1">Your website address (optional)</label>
              <input
                type="url"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                placeholder="e.g. www.myagency.com"
                className="w-full bg-slate-50 border border-slate-200 px-3 py-2.5 text-sm text-slate-600 focus:border-[#E07A3A] focus:ring-1 focus:ring-[#E07A3A] outline-none"
                data-testid="website-url-input"
              />
            </div>
            <Button
              onClick={requestEmbed}
              disabled={sending}
              className="bg-[#E07A3A] text-white hover:bg-[#C4652E] text-xs tracking-wider px-6 py-5"
              data-testid="request-embed-btn"
            >
              {sending ? 'Sending...' : 'Request Website Widget Setup'}
            </Button>
            <p className="text-[10px] text-slate-400">Free service — we handle everything. Just tell us your website and we'll do the rest.</p>
          </div>
          );
        })()}
      </div>

      {/* No guide here - moved to main dashboard bottom */}
    </div>
  );
};

const STATUS_STYLES = {
  new: 'bg-blue-50 text-blue-700',
  contacted: 'bg-amber-50 text-amber-700',
  in_progress: 'bg-violet-50 text-violet-700',
  completed: 'bg-green-50 text-green-700',
  declined: 'bg-red-50 text-red-500',
};

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'declined', label: 'Declined' },
];

const KypOnboardingBanner = ({ profile }) => {
  const s = profile?.status;
  // Not submitted
  if (!profile) {
    return (
      <div className="mb-8 bg-amber-50 border border-amber-300 p-4 flex flex-wrap items-center gap-3" data-testid="kyp-banner-none">
        <div className="flex-1 min-w-[240px]">
          <p className="text-amber-900 font-semibold text-sm">Complete your KYP verification to start accepting consultations</p>
          <p className="text-amber-800 text-xs mt-1">Upload your statutory licence (Bar Council / RERA / ICAI / SRA / RICS etc.) and sign the NDA. Takes 5 minutes.</p>
        </div>
        <Link to="/professional/onboard" className="bg-[#E07A3A] text-white hover:bg-[#C4652E] px-4 py-2 text-xs font-semibold" data-testid="kyp-banner-start-btn">
          Start KYP →
        </Link>
      </div>
    );
  }
  const palette = {
    pending: { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-900', msg: 'Your KYP is awaiting admin review — you\'ll be notified once verified (usually 1-2 business days).' },
    verified: { bg: 'bg-green-50', border: 'border-green-300', text: 'text-green-900', msg: '✓ KYP verified — you are live on Shopperden. Keep your fee schedule up to date.' },
    rejected: { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-900', msg: `KYP rejected: ${profile.rejection_reason || 'See email for details'}. Please resubmit.` },
    suspended: { bg: 'bg-slate-100', border: 'border-slate-400', text: 'text-slate-800', msg: 'Your account is currently suspended. Contact support for details.' },
  }[s] || { bg: 'bg-slate-100', border: 'border-slate-300', text: 'text-slate-800', msg: 'Unknown status' };
  return (
    <div className={`mb-8 ${palette.bg} ${palette.border} border p-4 flex flex-wrap items-center gap-3`} data-testid={`kyp-banner-${s}`}>
      <div className="flex-1 min-w-[240px]">
        <p className={`${palette.text} font-semibold text-sm`}>KYP Status: {s}</p>
        <p className={`${palette.text} text-xs mt-1 opacity-90`}>{palette.msg}</p>
      </div>
      {s !== 'verified' && (
        <Link to="/professional/onboard" className="bg-[#0F172A] text-white hover:bg-[#1e293b] px-4 py-2 text-xs font-semibold">
          {s === 'rejected' ? 'Resubmit' : 'View details'} →
        </Link>
      )}
    </div>
  );
};

const ProfessionalDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useQueryTab('consultations', ['consultations','leads','access','availability','profile']);
  const [expandedLead, setExpandedLead] = useState(null);
  const [compliance, setCompliance] = useState({});
  const [loadingCompliance, setLoadingCompliance] = useState({});
  const [updatingLead, setUpdatingLead] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [respondingTo, setRespondingTo] = useState(null);
  const [responseMsg, setResponseMsg] = useState('');
  const [respondLoading, setRespondLoading] = useState(false);
  const [profile, setProfile] = useState({ bio: '', location: '', specializations: '', service_areas: [], years_experience: '', qualifications: '', phone: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [kypProfile, setKypProfile] = useState(null);
  const { user } = useAuth();
  const { formatPrice } = useCurrencySettings();

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/professional/dashboard`);
      setData(res.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchConsultations = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/consultations/received`);
      setConsultations(res.data);
    } catch (err) { logger.error('Failed to load consultations:', err); }
  }, []);

  useEffect(() => {
    fetchDashboard();
    fetchConsultations();
    // Fetch KYP onboarding status
    axios.get(`${API_URL}/api/professional-onboarding/me`).then(r => setKypProfile(r.data.profile)).catch(() => {});
  }, [fetchDashboard, fetchConsultations]);

  const respondToConsultation = async (id, accepted) => {
    setRespondLoading(true);
    try {
      await axios.put(`${API_URL}/api/consultations/${id}/respond`, {
        accepted,
        response_message: responseMsg
      });
      toast.success(accepted ? 'Consultation accepted! Buyer has been notified.' : 'Consultation declined.');
      setRespondingTo(null);
      setResponseMsg('');
      fetchConsultations();
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to respond');
    } finally {
      setRespondLoading(false);
    }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const payload = {
        bio: profile.bio,
        location: profile.location,
        specializations: profile.specializations,
        service_areas: profile.service_areas,
        years_experience: profile.years_experience ? parseInt(profile.years_experience) : null,
        qualifications: profile.qualifications,
        phone: profile.phone,
      };
      await axios.put(`${API_URL}/api/professional/profile`, payload);
      toast.success('Profile updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  // Load profile from user data on mount
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/auth/me`);
        const u = res.data;
        setProfile({
          bio: u.bio || '',
          location: u.location || '',
          specializations: u.specializations || '',
          service_areas: u.service_areas || [],
          years_experience: u.years_experience || '',
          qualifications: u.qualifications || '',
          phone: u.phone || '',
        });
      } catch (err) { logger.error('Failed to load profile:', err); }
    };
    loadProfile();
  }, []);

  const updateLeadStatus = async (leadId, status, notes = '') => {
    setUpdatingLead(leadId);
    try {
      await axios.put(`${API_URL}/api/professional/leads/${leadId}/status`, { status, notes });
      toast.success(`Lead updated to ${status.replace('_', ' ')}`);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update lead');
    } finally {
      setUpdatingLead(null);
    }
  };

  const viewCompliance = async (leadId) => {
    if (compliance[leadId]) {
      setExpandedLead(expandedLead === leadId ? null : leadId);
      return;
    }
    setLoadingCompliance(prev => ({ ...prev, [leadId]: true }));
    try {
      const res = await axios.get(`${API_URL}/api/professional/compliance/${leadId}`);
      setCompliance(prev => ({ ...prev, [leadId]: res.data }));
      setExpandedLead(leadId);
    } catch (error) {
      toast.error('Failed to load compliance data');
    } finally {
      setLoadingCompliance(prev => ({ ...prev, [leadId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0C] premium-theme">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="w-8 h-8 border-2 border-[#E07A3A] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const stats = data?.stats || {};
  const leads = data?.leads || [];
  const accessRecords = data?.access_records || [];
  const pendingConsults = consultations.filter(c => c.status === 'pending').length;

  const tabs = [
    { id: 'consultations', label: 'Client Enquiries', count: consultations.length, icon: <MessageSquare className="h-4 w-4" />, badge: pendingConsults },
    { id: 'leads', label: 'My Clients', count: leads.length, icon: <Users className="h-4 w-4" /> },
    { id: 'availability', label: 'My Diary', icon: <Calendar className="h-4 w-4" /> },
    { id: 'profile', label: 'My Profile', icon: <UserCog className="h-4 w-4" /> },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="professional-dashboard">
      <Header />
      <RoleGuideBanner currentSection="/professional" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* CLEAN HEADER — Lead Management first, upsells in drawer below. */}
        <div className="mb-2">
          <span className="text-xs tracking-[0.3em] text-[#E07A3A] mb-1 block uppercase font-medium">Professional Portal</span>
          <h1 className="font-['Playfair_Display'] text-2xl sm:text-4xl text-[#1A1A1A]" data-testid="professional-dashboard-title">Lead Management</h1>
          <p className="text-sm text-slate-500 mt-1">
            Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}. Manage your warm leads &amp; consultations.
          </p>
        </div>

        {/* KYP Onboarding Banner — important auth state, keep visible. */}
        <KypOnboardingBanner profile={kypProfile} />

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 mb-6">
          {[
            { label: 'Total Leads', value: stats.total_leads || 0, icon: <Users className="h-5 w-5" /> },
            { label: 'Active', value: stats.active_leads || 0, icon: <Clock className="h-5 w-5" />, highlight: true },
            { label: 'Completed', value: stats.completed_leads || 0, icon: <CheckCircle className="h-5 w-5" /> },
            { label: 'Properties Accessed', value: stats.total_access || 0, icon: <Eye className="h-5 w-5" /> },
          ].map((stat) => (
            <div key={stat.label} className={`luxury-card p-6 text-center ${stat.highlight && stat.value > 0 ? 'border-[#E07A3A]/30' : ''}`} data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="flex justify-center mb-3 text-[#E07A3A]">{stat.icon}</div>
              <p className="text-3xl font-['Playfair_Display'] text-[#E07A3A]">{stat.value}</p>
              <p className="text-slate-500 text-sm mt-1">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <DashboardTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          tabs={tabs.map(t => ({ ...t, shortLabel: { consultations: 'Enquiries', availability: 'Diary', leads: 'Clients', profile: 'Profile' }[t.id] || t.label, testId: `tab-${t.id}` }))}
        />

        <RecoveryDiscountBanner dataTestId="professional-recovery-banner" />

        {/* Consultations Tab */}
        {activeTab === 'consultations' && (
          <div className="space-y-4" data-testid="consultations-list">
            {consultations.length === 0 ? (
              <div className="luxury-card p-12 text-center">
                <MessageSquare className="h-10 w-10 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 text-lg font-['Playfair_Display']">No consultation requests yet</p>
                <p className="text-slate-400 text-sm mt-2">When buyers request your services through the Mortgage Guide, they'll appear here.</p>
              </div>
            ) : (
              consultations.map((c) => {
                const statusStyles = { pending: 'bg-amber-50 text-amber-700 border-amber-200', accepted: 'bg-emerald-50 text-emerald-700 border-emerald-200', declined: 'bg-red-50 text-red-500 border-red-200' };
                const StatusIcon = { pending: Clock, accepted: CheckCircle, declined: XCircle }[c.status] || Clock;
                return (
                  <div key={c.id} className={`luxury-card overflow-hidden ${c.status === 'pending' ? 'border-[#E07A3A]/30' : ''}`} data-testid={`consultation-${c.id}`}>
                    <div className="p-5">
                      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-1 flex-wrap">
                            <h3 className="text-[#1A1A1A] font-medium text-lg">{c.buyer_name}</h3>
                            <span className={`inline-flex items-center gap-1.5 px-3 py-0.5 text-xs tracking-wider uppercase font-medium border ${statusStyles[c.status]}`}>
                              <StatusIcon className="h-3.5 w-3.5" /> {c.status}
                            </span>
                          </div>
                          <p className="text-sm text-slate-500">{c.buyer_email}</p>
                          <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-400">
                            <span className="uppercase tracking-wider font-medium text-[#E07A3A]">{(c.service_type || '').replace(/_/g, ' ')}</span>
                            {c.country && <span className="flex items-center gap-1"><Globe className="h-3 w-3" />{c.country}</span>}
                            {c.created_at && <span><Clock className="h-3 w-3 inline mr-1" />{new Date(c.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                          </div>
                          {c.message && (
                            <div className="mt-3 bg-slate-50 p-3 border-l-2 border-[#E07A3A]">
                              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Buyer's Message</p>
                              <p className="text-sm text-slate-600 italic">"{c.message}"</p>
                            </div>
                          )}
                          {c.prequalification_summary && (
                            <div className="mt-3">
                              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">Pre-Qualification Data</p>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                {[
                                  { label: 'Max Borrowing', value: `${c.prequalification_summary.symbol || '£'}${(c.prequalification_summary.max_borrowing || 0).toLocaleString('en-GB')}` },
                                  { label: 'Monthly Payment', value: `${c.prequalification_summary.symbol || '£'}${(c.prequalification_summary.estimated_monthly_payment || 0).toLocaleString('en-GB')}` },
                                  { label: 'LTV', value: `${c.prequalification_summary.ltv_ratio || 0}%` },
                                  { label: 'Affordability', value: `${c.prequalification_summary.affordability_ratio || 0}%` },
                                ].map(item => (
                                  <div key={item.label} className="bg-white border border-slate-100 p-2 text-center">
                                    <p className="text-[10px] text-slate-400 uppercase tracking-wider">{item.label}</p>
                                    <p className="text-sm font-semibold text-[#0F172A]">{item.value}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Response Actions */}
                        {c.status === 'pending' && (
                          <div className="flex flex-col gap-2 flex-shrink-0 w-full sm:w-auto">
                            {respondingTo === c.id ? (
                              <div className="space-y-2 min-w-[200px]" data-testid={`respond-form-${c.id}`}>
                                <textarea
                                  value={responseMsg}
                                  onChange={(e) => setResponseMsg(e.target.value)}
                                  placeholder="Optional message to the buyer..."
                                  className="w-full border border-slate-200 p-2 text-sm focus:border-[#E07A3A] focus:outline-none resize-none h-16"
                                  data-testid={`response-message-${c.id}`}
                                />
                                <Button
                                  onClick={() => respondToConsultation(c.id, true)}
                                  disabled={respondLoading}
                                  className="w-full bg-emerald-600 text-white hover:bg-emerald-700 text-xs tracking-wider"
                                  data-testid={`accept-btn-${c.id}`}
                                >
                                  <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                                  {respondLoading ? 'Processing...' : 'Accept'}
                                </Button>
                                <Button
                                  onClick={() => respondToConsultation(c.id, false)}
                                  disabled={respondLoading}
                                  variant="outline"
                                  className="w-full border-red-200 text-red-500 hover:bg-red-50 text-xs tracking-wider"
                                  data-testid={`decline-btn-${c.id}`}
                                >
                                  <XCircle className="h-3.5 w-3.5 mr-1.5" />Decline
                                </Button>
                                <button onClick={() => { setRespondingTo(null); setResponseMsg(''); }} className="text-xs text-slate-400 hover:text-slate-600 text-center w-full">Cancel</button>
                              </div>
                            ) : (
                              <Button
                                onClick={() => setRespondingTo(c.id)}
                                className="bg-[#E07A3A] text-white hover:bg-[#C4652E] text-xs tracking-wider"
                                data-testid={`respond-btn-${c.id}`}
                              >
                                Respond <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                              </Button>
                            )}
                          </div>
                        )}
                        {c.status !== 'pending' && c.response_message && (
                          <div className="text-right flex-shrink-0">
                            <p className="text-xs text-slate-400">Your response:</p>
                            <p className="text-sm text-slate-600 italic max-w-[200px]">"{c.response_message}"</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="max-w-2xl" data-testid="profile-form">
            <div className="luxury-card p-8">
              <div className="flex items-center gap-3 mb-6">
                <UserCog className="h-6 w-6 text-[#E07A3A]" />
                <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A]">Edit Your Profile</h2>
              </div>
              <p className="text-sm text-slate-400 mb-6">This information is shown to buyers in the Professional Directory. A complete profile increases your chances of receiving consultation requests.</p>

              <div className="space-y-5">
                <div>
                  <Label className="text-xs tracking-wider uppercase text-slate-500 font-semibold">Bio / About You</Label>
                  <textarea
                    value={profile.bio}
                    onChange={(e) => setProfile(p => ({ ...p, bio: e.target.value }))}
                    placeholder="Tell buyers about your experience, approach, and what makes you different..."
                    className="w-full border border-slate-200 p-3 text-sm focus:border-[#E07A3A] focus:outline-none resize-none h-24 mt-1"
                    data-testid="profile-bio"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs tracking-wider uppercase text-slate-500 font-semibold">Location</Label>
                    <Input value={profile.location} onChange={(e) => setProfile(p => ({ ...p, location: e.target.value }))} placeholder="e.g. London, Manchester" data-testid="profile-location" />
                  </div>
                  <div>
                    <Label className="text-xs tracking-wider uppercase text-slate-500 font-semibold">Phone</Label>
                    <Input value={profile.phone} onChange={(e) => setProfile(p => ({ ...p, phone: e.target.value }))} placeholder="Contact number" data-testid="profile-phone" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs tracking-wider uppercase text-slate-500 font-semibold">Specializations</Label>
                    <Input value={profile.specializations} onChange={(e) => setProfile(p => ({ ...p, specializations: e.target.value }))} placeholder="e.g. First-time buyers, Buy-to-let" data-testid="profile-specializations" />
                  </div>
                  <div>
                    <Label className="text-xs tracking-wider uppercase text-slate-500 font-semibold">Years of Experience</Label>
                    <Input type="number" value={profile.years_experience} onChange={(e) => setProfile(p => ({ ...p, years_experience: e.target.value }))} placeholder="e.g. 10" data-testid="profile-years" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs tracking-wider uppercase text-slate-500 font-semibold">Qualifications</Label>
                  <Input value={profile.qualifications} onChange={(e) => setProfile(p => ({ ...p, qualifications: e.target.value }))} placeholder="e.g. CeMAP, ACCA, RICS" data-testid="profile-qualifications" />
                </div>
                <div>
                  <Label className="text-xs tracking-wider uppercase text-slate-500 font-semibold">Service Areas (countries)</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {['UK', 'IN', 'CA'].map(area => (
                      <button
                        key={area}
                        onClick={() => {
                          setProfile(p => ({
                            ...p,
                            service_areas: p.service_areas.includes(area)
                              ? p.service_areas.filter(a => a !== area)
                              : [...p.service_areas, area]
                          }));
                        }}
                        className={`px-4 py-2 text-xs font-semibold tracking-wider uppercase transition-all ${
                          profile.service_areas.includes(area) ? 'bg-[#E07A3A] text-white' : 'border border-slate-200 text-slate-500 hover:border-[#E07A3A]'
                        }`}
                        data-testid={`service-area-${area}`}
                      >
                        {area === 'UK' ? 'United Kingdom' : area === 'IN' ? 'India' : 'Canada'}
                      </button>
                    ))}
                  </div>
                </div>
                <Button
                  onClick={saveProfile}
                  disabled={savingProfile}
                  className="bg-[#E07A3A] text-white hover:bg-[#C4652E] px-8 py-5 text-sm tracking-widest uppercase font-semibold"
                  data-testid="save-profile-btn"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {savingProfile ? 'Saving...' : 'Save Profile'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Share & Embed Tab */}
        {activeTab === 'embed' && (
          <EmbedCodeSection />
        )}

        {/* Leads Tab */}
        {activeTab === 'leads' && (
          <div className="space-y-4" data-testid="leads-list">
            {leads.length === 0 ? (
              <div className="luxury-card p-12 text-center">
                <Briefcase className="h-10 w-10 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 text-lg font-['Playfair_Display']">No leads assigned yet</p>
                <p className="text-slate-400 text-sm mt-2">Leads appear here when you accept consultation requests or admin assigns clients to you.</p>
              </div>
            ) : (
              leads.map((lead) => (
                <div key={lead.id} className="luxury-card overflow-hidden" data-testid={`lead-${lead.id}`}>
                  <div className="p-5 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                    {/* Lead Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-[#1A1A1A] font-medium text-lg">{lead.client_name || 'Unknown Client'}</h3>
                        <span className={`px-3 py-0.5 text-xs tracking-wider uppercase font-medium ${STATUS_STYLES[lead.status] || 'bg-slate-50 text-slate-500'}`}>
                          {(lead.status || 'new').replace('_', ' ')}
                        </span>
                      </div>
                      {lead.client_email && <p className="text-slate-500 text-sm">{lead.client_email}</p>}
                      <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-400">
                        <span className="uppercase tracking-wider font-medium">{lead.service_type?.replace('_', ' ') || 'General'}</span>
                        {lead.property && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {lead.property.title} {lead.property.city ? `- ${lead.property.city}` : ''}
                          </span>
                        )}
                        {lead.property?.price && (
                          <span className="text-[#E07A3A] font-medium">{formatPrice(lead.property.price)}</span>
                        )}
                      </div>
                      {lead.notes && <p className="text-slate-400 text-sm mt-2 italic">"{lead.notes}"</p>}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      <div className="flex gap-2">
                        <select
                          value={lead.status || 'new'}
                          onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                          disabled={updatingLead === lead.id}
                          className="border border-slate-200 px-3 py-2 text-sm text-[#0F172A] focus:border-[#E07A3A] focus:outline-none bg-white"
                          data-testid={`lead-status-select-${lead.id}`}
                        >
                          {STATUS_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-[#E07A3A]/30 text-[#E07A3A] hover:bg-[#FEF3EC] text-xs tracking-wider"
                        onClick={() => viewCompliance(lead.id)}
                        disabled={loadingCompliance[lead.id]}
                        data-testid={`compliance-btn-${lead.id}`}
                      >
                        {loadingCompliance[lead.id] ? (
                          <div className="w-3 h-3 border border-[#E07A3A] border-t-transparent rounded-full animate-spin mr-1.5" />
                        ) : (
                          <Shield className="h-3.5 w-3.5 mr-1.5" />
                        )}
                        {expandedLead === lead.id ? 'Hide' : 'View'} Compliance
                        {expandedLead === lead.id ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
                      </Button>
                    </div>
                  </div>

                  {/* Compliance Trail */}
                  {expandedLead === lead.id && compliance[lead.id] && (
                    <div className="border-t border-slate-100 bg-slate-50/50 p-5" data-testid={`compliance-panel-${lead.id}`}>
                      <div className="flex items-center gap-2 mb-4">
                        <Shield className="h-4 w-4 text-[#E07A3A]" />
                        <h4 className="text-sm font-semibold text-[#1A1A1A] tracking-wider uppercase">Compliance Trail</h4>
                      </div>

                      {compliance[lead.id].client ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Client Profile */}
                          <div className="bg-white border border-slate-200 p-4 space-y-3">
                            <h5 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Client Profile</h5>
                            <div className="space-y-2">
                              {[
                                { label: 'Name', value: compliance[lead.id].client.name },
                                { label: 'Email', value: compliance[lead.id].client.email },
                                { label: 'Phone', value: compliance[lead.id].client.phone },
                                { label: 'Registered', value: compliance[lead.id].client.registered_at ? new Date(compliance[lead.id].client.registered_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A' },
                              ].map(field => (
                                <div key={field.label} className="flex justify-between text-sm">
                                  <span className="text-slate-400">{field.label}</span>
                                  <span className="text-[#1A1A1A] font-medium">{field.value || 'N/A'}</span>
                                </div>
                              ))}
                              <div className="flex justify-between text-sm">
                                <span className="text-slate-400">ID Verified</span>
                                <span className={`flex items-center gap-1 font-medium ${compliance[lead.id].client.id_verified ? 'text-green-600' : 'text-amber-500'}`}>
                                  {compliance[lead.id].client.id_verified ? <CheckCircle className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
                                  {compliance[lead.id].client.id_verified ? 'Verified' : 'Pending'}
                                </span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-slate-400">T&Cs Accepted</span>
                                <span className={`flex items-center gap-1 font-medium ${compliance[lead.id].client.terms_accepted ? 'text-green-600' : 'text-red-500'}`}>
                                  {compliance[lead.id].client.terms_accepted ? <CheckCircle className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                                  {compliance[lead.id].client.terms_accepted ? 'Accepted' : 'Not Accepted'}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Verification Details */}
                          <div className="bg-white border border-slate-200 p-4 space-y-3">
                            <h5 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Verification Details</h5>
                            {compliance[lead.id].verification ? (
                              <div className="space-y-2">
                                {[
                                  { label: 'Document Type', value: compliance[lead.id].verification.id_type?.replace('_', ' ') },
                                  { label: 'Status', value: compliance[lead.id].verification.status },
                                  { label: 'Submitted', value: compliance[lead.id].verification.submitted_at ? new Date(compliance[lead.id].verification.submitted_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A' },
                                ].map(field => (
                                  <div key={field.label} className="flex justify-between text-sm">
                                    <span className="text-slate-400">{field.label}</span>
                                    <span className="text-[#1A1A1A] font-medium capitalize">{field.value || 'N/A'}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-slate-400 text-sm">No verification record found</p>
                            )}

                            {/* DIATF Badge */}
                            <div className="mt-4 bg-[#0F172A] p-3">
                              <div className="flex items-center gap-2 mb-1">
                                <FileCheck className="h-4 w-4 text-[#E07A3A]" />
                                <span className="text-xs text-white/70 font-semibold uppercase tracking-wider">Compliance Framework</span>
                              </div>
                              <p className="text-xs text-white/50 leading-relaxed">
                                {compliance[lead.id].compliance_note}
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <p className="text-slate-400 text-sm">{compliance[lead.id].message || 'No client data available'}</p>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Availability Tab */}
        {activeTab === 'availability' && (
          <div data-testid="availability-tab">
            <AvailabilityCalendar />
          </div>
        )}

        {/* Access Records Tab */}
        {activeTab === 'access' && (
          <div className="space-y-4" data-testid="access-records-list">
            {accessRecords.length === 0 ? (
              <div className="luxury-card p-12 text-center">
                <Eye className="h-10 w-10 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 text-lg font-['Playfair_Display']">No access records</p>
                <p className="text-slate-400 text-sm mt-2">Properties you have paid to access will appear here.</p>
              </div>
            ) : (
              accessRecords.map((access) => (
                <div key={access.id} className="luxury-card p-5 flex flex-col sm:flex-row gap-4 items-start sm:items-center" data-testid={`access-${access.id}`}>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-[#1A1A1A] font-medium text-lg">{access.property?.title || 'Property'}</h3>
                    <div className="flex flex-wrap gap-3 mt-1.5 text-sm text-slate-500">
                      {access.property?.city && (
                        <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{access.property.city}</span>
                      )}
                      {access.property?.price && (
                        <span className="text-[#E07A3A] font-medium">{formatPrice(access.property.price)}</span>
                      )}
                    </div>
                    {access.property?.seller_name && (
                      <p className="text-slate-400 text-xs mt-2">Seller: {access.property.seller_name} ({access.property.seller_email})</p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className="px-3 py-1 bg-green-50 text-green-700 text-xs tracking-wider uppercase font-medium">Access Granted</span>
                    {access.granted_at && (
                      <p className="text-xs text-slate-400 mt-2">{new Date(access.granted_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Help & Guide — collapsible, at bottom */}
        <details className="mt-8" data-testid="pro-help-guide">
          <summary className="cursor-pointer flex items-center gap-2 text-sm text-[#E07A3A] font-semibold tracking-wider uppercase hover:text-[#C4652E] transition-colors py-3">
            <HelpCircle className="h-4 w-4" />
            Help & Getting Started Guide
          </summary>
          <div className="mt-3">
            <GettingStartedGuide role="professional" />
          </div>
        </details>

        {/* ─── TOOLS & OFFERS DRAWER — relocated upsells ─── */}
        <details
          className="mt-10 border border-slate-200 rounded-lg bg-white overflow-hidden"
          open
          data-testid="professional-tools-offers"
        >
          <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between bg-gradient-to-r from-[#FEF8EC] via-white to-[#FEF3EC] border-b border-slate-100 hover:bg-amber-50/40 transition-colors">
            <div className="flex items-center gap-3">
              <Activity className="h-5 w-5 text-[#E07A3A]" />
              <div>
                <p className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E07A3A]">Tools &amp; Offers</p>
                <p className="text-sm font-semibold text-[#0F172A]">Affiliate wallet, network &amp; share · click to collapse</p>
              </div>
            </div>
            <span className="text-[10px] text-slate-500 hidden sm:block">All optional</span>
          </summary>

          <div className="p-5 space-y-5">
            <ProAffiliateWallet dataTestId="professional-affiliate-wallet" />

            <LocalProsPanel
              role="professional"
              city={kypProfile?.city || ''}
              country={(kypProfile?.country || 'UK').toUpperCase()}
              dataTestId="professional-local-pros-panel"
            />

            <InviteTeaserCard />

            <AffiliateWidget dataTestId="professional-affiliate-widget" />
          </div>
        </details>
      </div>
    </div>
  );
};

export default ProfessionalDashboard;
